import React, { useEffect, useState, useRef } from 'react';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { createUsers, showUser, editUser, storeUser, updateUser } from '@app/api/user.api';
import { UserRequest } from '@app/models/user/UserRequest';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { Tooltip, Button, notification, Select, Checkbox, Form, Col, Row } from 'antd';
import { notificationController } from '@app/controllers/notificationController';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import StatusBar from '@app/components/statusBar/statusBar';
import { actionButtons, getButtonsByStatus } from '@app/components/statusBar/actionButtons';
import { useHeader } from '@app/components/header/layouts/HeaderContext';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface UserFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

interface TableDataType {
  id: string | number;
  name: string;
}

const UserForm: React.FC<UserFormProps> = ({ editable = false, id, pageTitle, type, setCurrentBreadcrumName }) => {
  const [form] = BaseButtonsForm.useForm();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = id ? parseInt(id) : null;
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [roleTableData, setRoleTableData] = useState<TableDataType[]>([]);
  const [departmentTableData, setDepartmentTableData] = useState<TableDataType[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string | number }[]>([]);
  const [deptOptions, setDeptOptions] = useState<{ label: string; value: string | number }[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const hasFetchedRef = useRef(false);

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setShowPasswordFields(e.target.checked);
    form.setFieldsValue({
      change_password: e.target.checked,
    });
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);

    const setUserData = (res: any) => {
      form.setFieldsValue({
        username: res.user.username,
        name: res.user.name,
        email: res.user.email,
        role_ids: res.user.roles.map((role: any) => role.id),
        department_ids: res.user.departments.map((dept: any) => dept.id),
        status: res.user.status,
      });

      setStatus(res.user.status);
      setCurrentBreadcrumName(res?.user?.name || 'New');

      const roleData = res.user.roles.map((role: any) => ({
        id: role.id,
        name: role.role_name,
      }));
      setRoleTableData(roleData);

      const departmentData = res.user.departments.map((dept: any) => ({
        id: dept.id,
        name: dept.name,
        code: dept.code,
        address: dept.address,
      }));
      setDepartmentTableData(departmentData);

      setLoading(false);
    };

    const setOptions = (res: any) => {
      const mappedRoles = res.role_options.map((role: any) => ({
        label: role.role_name,
        value: role.id,
      }));
      setRoleOptions(mappedRoles);

      const mappedDepts = res.department_options.map((dept: any) => ({
        label: dept.name,
        value: dept.id,
      }));
      setDeptOptions(mappedDepts);

      const mappedStatuses = res.status_options.map((status: any) => ({
        label: status.label,
        value: status.value,
      }));
      setStatusOptions(mappedStatuses);
    };

    const handleError = (err: any, message: string) => {
      setLoading(false);
      notification.error({ message, description: err.message });
      console.error(err);
    };

    if (userId) {
      if (type === 'view') {
        showUser(userId)
          .then(setUserData)
          .catch((err) => handleError(err, 'Failed to view user'));
      } else if (type === 'edit') {
        editUser(userId)
          .then((res: any) => {
            setOptions(res);
            setUserData(res);
          })
          .catch((err) => handleError(err, 'Failed to edit user'));
      }
    } else if (type === 'create') {
      createUsers()
        .then(setOptions)

        .catch((err) => handleError(err, 'Failed to fetch options'))
        .finally(() => setLoading(false));
    }
  }, [id, type, setCurrentBreadcrumName]);

  const handleSubmit = (values: UserRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        department_ids: values.department_ids.map((id: { toString: () => string }) => parseInt(id.toString(), 10)),
      };

      if (type === 'create') {
        storeUser(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/users`);
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
            console.error(err);
          });
      } else if (type === 'edit' && userId) {
        updateUser(userId, submissionData)
          .then(() => {
            setLoading(false);
            navigate(`/users`);
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
            console.error(err);
          });
      }
    }
  };

  const handleAddRoles = (values: (string | number)[]) => {
    const newRoles = values
      .map((value) => {
        const role = roleOptions.find((r) => r.value === value);
        return role ? { id: role.value, name: role.label } : { id: value, name: '' };
      })
      .filter((newRole) => !roleTableData.some((existingRole) => existingRole.id === newRole.id));

    const updatedTableData = [...roleTableData, ...newRoles];
    setRoleTableData(updatedTableData);

    form.setFieldValue(
      'role_ids',
      updatedTableData.map((role) => role.id),
    );
  };

  const handleAddDepartments = (values: (string | number)[]) => {
    const newDepartments = values
      .map((value) => {
        const dept = deptOptions.find((r) => r.value === value);
        return dept ? { id: dept.value, name: dept.label } : { id: value, name: '' };
      })
      .filter((newDept) => !departmentTableData.some((existingDept) => existingDept.id === newDept.id));

    const updatedTableData = [...departmentTableData, ...newDepartments];
    setDepartmentTableData(updatedTableData);

    form.setFieldValue(
      'department_ids',
      updatedTableData.map((dept) => dept.id),
    );
  };

  const handleRemoveRole = (id: string | number) => {
    const updatedTableData = roleTableData.filter((r) => r.id !== id);
    setRoleTableData(updatedTableData);

    form.setFieldValue(
      'role_ids',
      updatedTableData.map((role) => role.id),
    );
  };

  const handleRemoveDepartment = (id: string | number) => {
    const updatedTableData = departmentTableData.filter((d) => d.id !== id);
    setDepartmentTableData(updatedTableData);

    form.setFieldValue(
      'department_ids',
      updatedTableData.map((dept) => dept.id),
    );
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton
          type="primary"
          htmlType="submit"
          loading={isLoading}
          onClick={() => handleSubmit(form.getFieldsValue())}
          size="small"
        >
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/users')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-user`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }
    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const rolecolumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_: any, record: { id: any }) => (
        <Button onClick={() => handleRemoveRole(record.id)} disabled={!editable}>
          Remove
        </Button>
      ),
    },
  ];

  const departmentColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_: any, record: { id: any }) => (
        <Button onClick={() => handleRemoveDepartment(record.id)} disabled={!editable}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar
            id={String(userId)}
            status={status}
            wfActions={[]}
            currentType={'create'}
            workflowType={''}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onViewInvoicesClick={() => {}}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setStatus={() => {}}
          />
        </BaseCol>
        <br />
        <BaseCard
          id="rolePermissionsForm"
          title={pageTitle}
          padding="1.25rem"
          style={{ margin: '20px', marginTop: '0px', width: '98%' }}
          // extra={
          //   type === 'view' && (
          //     <Tooltip title="Edit">
          //       <Button type="ghost" onClick={() => navigate(`/edit-user`, { state: { id } })}>
          //         Edit
          //       </Button>
          //     </Tooltip>
          //   )
          // }
        >
          <Form
            form={form}
            name="userForm"
            labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
            labelAlign="left"
            labelWrap
            wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
            size="small"
            style={{ marginLeft: '40px' }}
          >
            <Row gutter={16}>
              <Col xs={24} md={24} lg={12}>
                <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                  <BaseInput readOnly={!editable} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                  <BaseInput readOnly={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                  <BaseInput readOnly={!editable} />
                </Form.Item>
              </Col>
            </Row>
            {type !== 'view' && (
              <>
                {type === 'edit' && (
                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="change_password" valuePropName="checked">
                        <Checkbox onChange={handleCheckboxChange}>
                          <span style={{ color: '#BA2504' }}>Click here to change the password</span>
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {(type === 'create' || (type === 'edit' && showPasswordFields)) && (
                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                          { required: true },
                          {
                            pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[a-zA-Z]).{8,}$/,
                            message:
                              'Password must contain at least 8 characters including an uppercase letter, a special character, and a number',
                          },
                        ]}
                      >
                        <BaseInput.Password readOnly={!editable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        name="password_confirmation"
                        label="Confirm Password"
                        dependencies={['password']}
                        rules={[
                          { required: true, message: 'Please confirm your password!' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Passwords do not match!'));
                            },
                          }),
                        ]}
                      >
                        <BaseInput.Password readOnly={!editable} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </>
            )}

            <Row gutter={16}>
              {type !== 'view' && (
                <>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="department_ids" label="Departments" rules={[{ required: true }]}>
                      <BaseSelect
                        mode="multiple"
                        disabled={!editable}
                        options={deptOptions}
                        onChange={(value) => handleAddDepartments(value as (string | number)[])}
                        value={departmentTableData.map((dept) => dept.id)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="role_ids" label="Roles">
                      <BaseSelect
                        mode="multiple"
                        disabled={!editable}
                        options={roleOptions}
                        onChange={(value) => handleAddRoles(value as (string | number)[])}
                        value={roleTableData.map((role) => role.id)}
                        style={{ marginLeft: '-60px' }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col xs={24} md={24} lg={12}>
                <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  {!editable ? (
                    <BaseInput
                      readOnly
                      value={statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label || ''}
                    />
                  ) : (
                    <BaseSelect disabled={!editable} options={statusOptions} />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <br />
            <BaseRow gutter={[16, 16]}>
              <BaseCol span={24}>
                <BaseTable
                  title={() => 'Departments'}
                  columns={departmentColumns}
                  dataSource={departmentTableData}
                  rowKey="id"
                  size="small"
                  bordered
                  scroll={{ x: true }}
                  pagination={{
                    pageSize: 5,
                    showSizeChanger: false,
                    defaultCurrent: 1,
                  }}
                />
              </BaseCol>
            </BaseRow>
            <br />

            <BaseRow gutter={[16, 16]}>
              <BaseCol span={24}>
                <BaseTable
                  title={() => 'Roles'}
                  columns={rolecolumns}
                  dataSource={roleTableData}
                  rowKey="id"
                  size="small"
                  bordered
                  scroll={{ x: true }}
                  pagination={{
                    pageSize: 5,
                    showSizeChanger: false,
                    defaultCurrent: 1,
                  }}
                />
              </BaseCol>
            </BaseRow>
            <br />
            {/* <BaseButtonsForm.Item>
            <BaseButton type="primary" htmlType="submit" loading={isLoading} disabled={!editable}>
              {t('Save User')}
            </BaseButton>
          </BaseButtonsForm.Item> */}
          </Form>
        </BaseCard>
      </BaseRow>
    </>
  );
};

export default UserForm;
